/*
 * mm-reviews.js
 * Shared Judge.me data layer + rendering helpers for:
 *   sections/mm-reviews-featured.liquid (homepage)
 *   sections/mm-reviews-product.liquid (product pages)
 *
 * Verified data layer (CEO-tested 2026-07-20):
 *   GET https://cdn.judge.me/reviews/reviews_for_carousel
 *     ?shop_domain=...&platform=shopify&max_reviews=100&star_rating=all
 *     &display_order=most_recent&carousel_type=cards
 *     &reviews_selection=current_product&product_ids={id}
 *   Public, no token, CORS-open. Do NOT use cache.judge.me or
 *   api.judge.me widget endpoints — they fail for this shop.
 *
 * All review text is rendered by the calling sections via textContent
 * (never innerHTML on review data). The SVG helpers in this file build
 * markup from fixed, non-user-controlled string constants only.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.MMReviews = factory();
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var CACHE_MINUTES = 30;

  var STAR_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
  var STAR_SVG_EMPTY =
    '<svg viewBox="0 0 24 24" aria-hidden="true" class="mmr-star--empty"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
  var CHECK_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
  var BADGE_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>';

  /* Dark greens only. The palette used to include #de7b52 and #d85f32, which
   forced an impossible choice: white fails on the oranges (2.97:1) and dark
   fails on the greens (1.18:1). With greens only, white passes everywhere,
   worst case 5.87:1. It also stops ember being used decoratively. */
var PALETTE = ["#2b5e51", "#1a3a2e", "#4d6b5e", "#102c23"];

  function endpointUrl(shopDomain, productId) {
    return (
      "https://cdn.judge.me/reviews/reviews_for_carousel" +
      "?shop_domain=" +
      encodeURIComponent(shopDomain) +
      "&platform=shopify&max_reviews=100&star_rating=all" +
      "&display_order=most_recent&carousel_type=cards" +
      "&reviews_selection=current_product&product_ids=" +
      encodeURIComponent(productId)
    );
  }

  function cacheKey(productId) {
    return "mm_jdgm_reviews_" + productId;
  }

  function clearCache(productId) {
    try {
      localStorage.removeItem(cacheKey(productId));
    } catch (e) {}
  }

  function normalizeReview(raw) {
    raw = raw || {};
    var name = raw.is_anonymous_reviewer
      ? "Anonymous"
      : String(raw.reviewer_name || "Customer").trim() || "Customer";
    return {
      uuid: raw.uuid || "",
      rating: Number(raw.rating) || 0,
      reviewerName: name,
      reviewerInitial: raw.reviewer_initial || name.charAt(0) || "?",
      verifiedBuyer: !!raw.verified_buyer,
      title: raw.title || "",
      body: raw.body || "",
      createdAt: raw.created_at || null,
      picturesUrls: raw.pictures_urls || [],
      pinned: raw.pinned === true,
      thumbUp: Number(raw.thumb_up) || 0,
      productTitle: raw.product_title || "",
      isAnonymous: !!raw.is_anonymous_reviewer,
      locationCountryCode: raw.location_country_code || ""
    };
  }

  function computeAggregate(reviews) {
    reviews = reviews || [];
    var count = reviews.length;
    if (!count) return { average: 0, count: 0 };
    var sum = 0;
    for (var i = 0; i < count; i++) sum += reviews[i].rating || 0;
    var average = Math.round((sum / count) * 10) / 10;
    return { average: average, count: count };
  }

  function histogram(reviews) {
    var h = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    (reviews || []).forEach(function (r) {
      var s = Math.min(5, Math.max(1, Math.round(r.rating || 0)));
      h[s] = (h[s] || 0) + 1;
    });
    return h;
  }

  function percentPositive(reviews) {
    reviews = reviews || [];
    if (!reviews.length) return 0;
    var pos = reviews.filter(function (r) {
      return r.rating >= 4;
    }).length;
    return Math.round((pos / reviews.length) * 100);
  }

  function pickFeatured(reviews, featuredName) {
    reviews = reviews || [];
    if (!reviews.length) return -1;

    if (featuredName) {
      var target = String(featuredName).trim().toLowerCase();
      if (target) {
        for (var i = 0; i < reviews.length; i++) {
          if (String(reviews[i].reviewerName).trim().toLowerCase() === target) return i;
        }
      }
    }

    for (var p = 0; p < reviews.length; p++) {
      if (reviews[p].pinned) return p;
    }

    var best = -1;
    var bestLen = -1;
    reviews.forEach(function (r, idx) {
      if (r.rating === 5) {
        var len = (r.body || "").length;
        if (len > bestLen) {
          bestLen = len;
          best = idx;
        }
      }
    });
    return best === -1 ? 0 : best;
  }

  function sortReviews(reviews, mode) {
    var list = (reviews || []).slice();
    function byRecent(a, b) {
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    }
    if (mode === "highest") {
      list.sort(function (a, b) {
        return b.rating - a.rating || byRecent(a, b);
      });
    } else if (mode === "lowest") {
      list.sort(function (a, b) {
        return a.rating - b.rating || byRecent(a, b);
      });
    } else {
      list.sort(byRecent);
    }
    return list;
  }

  function filterReviews(reviews, query) {
    var q = String(query || "").trim().toLowerCase();
    if (!q) return (reviews || []).slice();
    return (reviews || []).filter(function (r) {
      var hay = (r.title + " " + r.body + " " + r.reviewerName).toLowerCase();
      return hay.indexOf(q) !== -1;
    });
  }

  function initials(name) {
    var parts = String(name || "?").trim().split(/\s+/);
    var first = parts[0] ? parts[0].charAt(0) : "?";
    var last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
    return (first + last).toUpperCase();
  }

  function colorFor(name) {
    var hash = 0;
    var str = String(name || "customer");
    for (var i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    }
    return PALETTE[hash % PALETTE.length];
  }

  function relativeDate(iso) {
    if (!iso) return "";
    var days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (isNaN(days) || days < 1) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return days + " days ago";
    var months = Math.floor(days / 30);
    if (days < 365) return months + (months === 1 ? " month ago" : " months ago");
    var years = Math.floor(days / 365);
    return years + (years === 1 ? " year ago" : " years ago");
  }

  function monthYear(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    } catch (e) {
      return "";
    }
  }

  function isDesignMode() {
    return !!(typeof Shopify !== "undefined" && Shopify.designMode);
  }

  /* ---- DOM builders (fixed SVG markup only; review text stays textContent in callers) ---- */

  function createStars(rating, className) {
    var span = document.createElement("span");
    span.className = "mmr-stars" + (className ? " " + className : "");
    span.setAttribute("role", "img");
    var rounded = Math.max(0, Math.min(5, Math.round(rating || 0)));
    span.setAttribute("aria-label", rounded + " out of 5 stars");
    var html = "";
    for (var i = 0; i < 5; i++) html += i < rounded ? STAR_SVG : STAR_SVG_EMPTY;
    span.innerHTML = html;
    return span;
  }

  function createVerifiedTick() {
    var span = document.createElement("span");
    span.className = "mmr-verified-tick";
    span.innerHTML = CHECK_SVG;
    return span;
  }

  function createVerifiedPill(text) {
    var span = document.createElement("span");
    span.className = "mmr-verified-pill";
    span.innerHTML = BADGE_SVG;
    var label = document.createElement("span");
    label.textContent = text;
    span.appendChild(label);
    return span;
  }

  function createAvatar(name, className) {
    var div = document.createElement("div");
    div.className = className || "mmr-avatar";
    div.style.background = colorFor(name);
    div.textContent = initials(name);
    return div;
  }

  /* ---- data layer ---- */

  function fetchReviews(shopDomain, productId, opts) {
    opts = opts || {};
    var key = cacheKey(productId);
    if (!opts.force) {
      try {
        var hit = JSON.parse(localStorage.getItem(key) || "null");
        if (hit && Date.now() - hit.t < CACHE_MINUTES * 60000) {
          return Promise.resolve(hit.data);
        }
      } catch (e) {}
    }

    return fetch(endpointUrl(shopDomain, productId), { credentials: "omit" })
      .then(function (res) {
        if (!res.ok) throw new Error("Judge.me responded " + res.status);
        return res.json();
      })
      .then(function (json) {
        var raw = (json && json.reviews) || [];
        var reviews = raw.map(normalizeReview);
        var agg = computeAggregate(reviews);
        var data = { reviews: reviews, average: agg.average, count: agg.count };
        try {
          localStorage.setItem(key, JSON.stringify({ t: Date.now(), data: data }));
        } catch (e) {}
        return data;
      });
  }

  function parseProductIds(productIds) {
    var list = Array.isArray(productIds) ? productIds : String(productIds || "").split(",");
    var out = [];
    list.forEach(function (id) {
      var trimmed = String(id).trim();
      if (trimmed) out.push(trimmed);
    });
    return out;
  }

  /* fetchReviewsMulti: the cdn.judge.me endpoint does not reliably merge
     multiple product_ids in one request (confirmed by hand: repeated
     product_ids params keep only the last value, and comma-joined ids are
     parsed as a single leading integer, so only the first id's reviews come
     back). Fetch each product id separately instead and merge client-side,
     deduped by uuid. */
  function fetchReviewsMulti(shopDomain, productIds, opts) {
    var ids = parseProductIds(productIds);
    if (!ids.length) return Promise.resolve({ reviews: [], average: 0, count: 0 });

    return Promise.all(
      ids.map(function (id) {
        return fetchReviews(shopDomain, id, opts).catch(function () {
          return { reviews: [], average: 0, count: 0 };
        });
      })
    ).then(function (results) {
      var seen = {};
      var merged = [];
      results.forEach(function (data) {
        (data.reviews || []).forEach(function (review) {
          var key = review.uuid || [review.reviewerName, review.createdAt, review.body].join("|");
          if (seen[key]) return;
          seen[key] = true;
          merged.push(review);
        });
      });
      merged = sortReviews(merged, "recent");
      var agg = computeAggregate(merged);
      return { reviews: merged, average: agg.average, count: agg.count };
    });
  }

  function submitReview(shopDomain, productId, payload) {
    var body = {
      shop_domain: shopDomain,
      platform: "shopify",
      id: parseInt(productId, 10),
      name: payload.name,
      email: payload.email,
      rating: payload.rating,
      title: payload.title,
      body: payload.body
    };
    return fetch("https://judge.me/api/v1/reviews", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error("Review submit failed " + res.status);
      clearCache(productId);
      return res;
    });
  }

  return {
    CACHE_MINUTES: CACHE_MINUTES,
    STAR_SVG: STAR_SVG,
    CHECK_SVG: CHECK_SVG,
    BADGE_SVG: BADGE_SVG,
    normalizeReview: normalizeReview,
    computeAggregate: computeAggregate,
    histogram: histogram,
    percentPositive: percentPositive,
    pickFeatured: pickFeatured,
    sortReviews: sortReviews,
    filterReviews: filterReviews,
    initials: initials,
    colorFor: colorFor,
    relativeDate: relativeDate,
    monthYear: monthYear,
    isDesignMode: isDesignMode,
    createStars: createStars,
    createVerifiedTick: createVerifiedTick,
    createVerifiedPill: createVerifiedPill,
    createAvatar: createAvatar,
    fetchReviews: fetchReviews,
    fetchReviewsMulti: fetchReviewsMulti,
    parseProductIds: parseProductIds,
    submitReview: submitReview,
    clearCache: clearCache,
    endpointUrl: endpointUrl
  };
});
